// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// rules.ts — the whole game, as pure data and pure functions. No DOM, no timers, no randomness.
//
// The shape of the thing: there is no board. A position is a set of occupied hexes, each holding a
// STACK of tiles (bottom first, top last), plus each side's remaining supply. Every rule in brood
// falls out of two constraints and five movement laws:
//
//   THE ONE-CLUSTER RULE   the occupied cells must form a single connected component, both while a
//                          tile is in the air and after it lands. Implemented as an articulation-
//                          point (cut-vertex) scan of the occupied graph, computed ONCE per
//                          position rather than once per candidate move — it is the same answer
//                          for all of them, and it is most of what move generation costs.
//   THE SLIDE RULE         a tile that walks physically slides between two neighbours, so it
//                          cannot pass a gap whose two flanking cells are BOTH occupied — and it
//                          must keep touching the cluster as it goes, so it cannot cross a gap
//                          whose flanks are both EMPTY either. Exactly one flank occupied is the
//                          legal case, which is why the test is a xor. The Vaulter (which jumps)
//                          and the Climber (which goes over the top) are exempt by definition, and
//                          the Climber has a height-based version of the same idea instead.
//
// Two implementation decisions carry the search, and both are worth the ugliness:
//
//   * CELL-INDEXED TYPED ARRAYS, not a Map. Occupancy, the top tile and the dense-list slot are
//     three flat arrays indexed by the packed hex key, so every probe is a load and stepping to a
//     neighbour is an addition. A Map costs a hash per probe and, more importantly, `neighbours()`
//     allocating a six-element array per call turns into megabytes of garbage at a hundred
//     thousand nodes.
//   * MAKE / UNMAKE, not copy. apply() returns the record undo() needs, and nothing else ever
//     writes to the occupancy. Cloning a position per node would be most of the move budget.

import { CELLS, DIR_OFF, dirBetween, gateA, gateB, key, keyQ, keyR, neighbours, step } from './hex';
import { CLIMBER, KIND_COUNT, MATRIARCH, STRIDER, TENDER, VAULTER, WEAVER, tile, tileKind, tileSeat } from './kinds';
import type { Mode } from './modes';

export type { Mode };

/** The most tiles that can ever stand on the table. Sizes the dense occupied-cell list. */
export const MAX_CELLS = 32;

// ---------------------------------------------------------------- moves

export type Move =
  | { readonly t: 'place'; readonly kind: number; readonly to: number }
  | { readonly t: 'move'; readonly from: number; readonly to: number }
  | { readonly t: 'shunt'; readonly tender: number; readonly from: number; readonly to: number }
  | { readonly t: 'pass' };

/**
 * A move packed into one integer, which is what travels on the wire. A whole game is a few dozen
 * of these, so every message can carry the ENTIRE log and a dropped datagram becomes unobservable.
 *
 * The layout IS the protocol. Do not renumber it.
 *   tag 0  place  kind:3 @2    to:14 @5
 *   tag 1  move   from:14 @2   to:14 @16
 *   tag 2  pass
 *   tag 3  shunt  tender:14 @2 fromDir:3 @16  toDir:3 @19
 *
 * A shunt stores DIRECTIONS rather than cells because both the lifted tile and its destination are
 * neighbours of the Tender by definition — three bits each instead of fourteen, which is what
 * keeps every ply inside one 30-bit integer.
 */
export function plyOf(m: Move): number {
  switch (m.t) {
    case 'place':
      return 0 | (m.kind << 2) | (m.to << 5);
    case 'move':
      return 1 | (m.from << 2) | (m.to << 16);
    case 'pass':
      return 2;
    case 'shunt':
      return 3 | (m.tender << 2) | (dirBetween(m.tender, m.from) << 16) | (dirBetween(m.tender, m.to) << 19);
  }
}

/** The inverse of plyOf. Returns null for anything that is not a well-formed ply. */
export function moveOf(ply: number): Move | null {
  if (!Number.isInteger(ply) || ply < 0 || ply > 0x3fffffff) return null;
  switch (ply & 3) {
    case 0: {
      const kind = (ply >> 2) & 7;
      const to = (ply >> 5) & 0x3fff;
      if (kind >= KIND_COUNT) return null;
      return { t: 'place', kind, to };
    }
    case 1:
      return { t: 'move', from: (ply >> 2) & 0x3fff, to: (ply >> 16) & 0x3fff };
    case 2:
      return { t: 'pass' };
    default: {
      const tender = (ply >> 2) & 0x3fff;
      const fd = (ply >> 16) & 7;
      const td = (ply >> 19) & 7;
      if (fd > 5 || td > 5 || fd === td) return null;
      return { t: 'shunt', tender, from: step(tender, fd), to: step(tender, td) };
    }
  }
}

export const sameMove = (a: Move, b: Move): boolean => plyOf(a) === plyOf(b);

// ---------------------------------------------------------------- state

export interface Undo {
  readonly m: Move;
  readonly stunA: number;
  readonly stunB: number;
  readonly stunUntil: number;
  readonly passes: number;
}

export class Position {
  readonly mode: Mode;
  /** Stack height at each cell. 0 is empty. */
  readonly occ = new Uint8Array(CELLS);
  /** The tile on top of each cell, or -1. */
  readonly topT = new Int8Array(CELLS).fill(-1);
  /** The FULL stack for cells with two or more tiles, bottom-first. Climbs are rare, so this is
   *  almost always empty and never touched by the hot loops. */
  readonly deep = new Map<number, number[]>();
  /** The dense list of occupied cells, and each cell's index into it. Iterating the cluster is
   *  therefore a walk of `n` integers rather than a Map iteration. */
  readonly list = new Int32Array(MAX_CELLS);
  readonly slot = new Int16Array(CELLS).fill(-1);
  n = 0;

  /** hands[seat][kind] — tiles still to be placed. */
  readonly hands: [number[], number[]];
  turn: 0 | 1 = 0;
  ply = 0;
  /** Where each Matriarch stands, or -1 while she is still in hand. */
  matriarch: [number, number] = [-1, -1];
  /**
   * After a lift, the tile that was moved and the Tender that moved it are both held still for the
   * next two plies. Without this, Retinue is a shunt loop: one Tender puts a tile down, the other
   * puts it straight back, and the ply cap eats every game.
   */
  stunA = -1;
  stunB = -1;
  stunUntil = -1;
  /** Consecutive passes. Two in a row means neither side can act, and the game is a draw. */
  passes = 0;

  /** Cut-vertex flags, indexed by dense SLOT rather than by cell, and rewritten in place by
   *  computeCuts. Preallocated because it is touched once per search node. */
  readonly cut = new Uint8Array(MAX_CELLS);
  private cutPly = -1;
  private cutStamp = -1;

  constructor(mode: Mode) {
    this.mode = mode;
    this.hands = [mode.supply.slice(), mode.supply.slice()];
  }

  height(k: number): number {
    return this.occ[k];
  }
  occupied(k: number): boolean {
    return this.occ[k] !== 0;
  }
  top(k: number): number {
    return this.topT[k];
  }
  /** The seat controlling a cell — the owner of the tile on top of it. -1 when empty. */
  owner(k: number): number {
    const t = this.topT[k];
    return t < 0 ? -1 : t & 1;
  }
  /** The whole stack at a cell, bottom-first. Allocates for stacked cells; view code only. */
  stack(k: number): number[] {
    if (this.occ[k] === 0) return [];
    return this.deep.get(k) ?? [this.topT[k]];
  }
  /** Every occupied cell, as a fresh array. View and test code only — the search walks `list`. */
  occupiedCells(): number[] {
    return Array.from(this.list.subarray(0, this.n));
  }
  /** Is this cell being held still by a recent lift? */
  stunned(k: number): boolean {
    return this.ply < this.stunUntil && (k === this.stunA || k === this.stunB);
  }

  /** Mark the cut-vertex cache stale. Called by every mutation. */
  invalidate(): void {
    this.cutStamp = -1;
  }
  /** True when `cut` already describes this exact position. */
  cutsFresh(): boolean {
    return this.cutStamp === this.ply && this.cutPly === this.n;
  }
  markCuts(): void {
    this.cutStamp = this.ply;
    this.cutPly = this.n;
  }

  // -- occupancy mutation. The ONLY writers of occ/topT/deep/list/slot. --

  /** Take the top tile off a cell and return it. */
  liftTile(k: number): number {
    const h = this.occ[k];
    const t = this.topT[k];
    if (h === 1) {
      this.occ[k] = 0;
      this.topT[k] = -1;
      const s = this.slot[k];
      const last = this.list[--this.n];
      this.list[s] = last;
      this.slot[last] = s;
      this.slot[k] = -1;
    } else {
      const st = this.deep.get(k)!;
      st.pop();
      this.occ[k] = h - 1;
      this.topT[k] = st[st.length - 1];
      if (st.length === 1) this.deep.delete(k);
    }
    this.invalidate();
    return t;
  }

  /** Put a tile on top of a cell. */
  dropTile(k: number, t: number): void {
    const h = this.occ[k];
    if (h === 0) {
      this.occ[k] = 1;
      this.topT[k] = t;
      this.slot[k] = this.n;
      this.list[this.n++] = k;
    } else {
      let st = this.deep.get(k);
      if (st === undefined) {
        st = [this.topT[k]];
        this.deep.set(k, st);
      }
      st.push(t);
      this.occ[k] = h + 1;
      this.topT[k] = t;
    }
    this.invalidate();
  }

  clone(): Position {
    const p = new Position(this.mode);
    p.occ.set(this.occ);
    p.topT.set(this.topT);
    p.list.set(this.list);
    p.slot.set(this.slot);
    p.n = this.n;
    for (const [k, s] of this.deep) p.deep.set(k, s.slice());
    p.hands[0] = this.hands[0].slice();
    p.hands[1] = this.hands[1].slice();
    p.turn = this.turn;
    p.ply = this.ply;
    p.matriarch = [this.matriarch[0], this.matriarch[1]];
    p.stunA = this.stunA;
    p.stunB = this.stunB;
    p.stunUntil = this.stunUntil;
    p.passes = this.passes;
    return p;
  }
}

// ---------------------------------------------------------------- connectivity

// Scratch for the articulation scan, module-level and reused. The search runs this once per node,
// so allocating here would be allocating a hundred thousand times a second.
const dDisc = new Int16Array(MAX_CELLS);
const dLow = new Int16Array(MAX_CELLS);
const dPar = new Int16Array(MAX_CELLS);
const dCell = new Int32Array(MAX_CELLS);
const dIter = new Int8Array(MAX_CELLS);
const dStack = new Int16Array(MAX_CELLS + 1);

/**
 * Fill `p.cut` — the cells whose removal would split the cluster in two. A lone tile on such a
 * cell can never move; that IS the one-cluster rule.
 *
 * Hopcroft–Tarjan lowlink, written iteratively so a pathological cluster can never blow the JS
 * stack, and indexed by dense slot so the scratch is 32 entries rather than 16384.
 */
export function computeCuts(p: Position): Uint8Array {
  if (p.cutsFresh()) return p.cut;
  const n = p.n;
  p.cut.fill(0, 0, n);
  p.markCuts();
  if (n <= 2) return p.cut;

  for (let i = 0; i < n; i++) {
    dDisc[i] = -1;
    dPar[i] = -1;
    dIter[i] = 0;
    dCell[i] = p.list[i];
  }
  let timer = 0;
  let sp = 0;
  dStack[sp++] = 0;
  dDisc[0] = timer;
  dLow[0] = timer++;
  let rootKids = 0;
  let seen = 1;

  while (sp > 0) {
    const v = dStack[sp - 1];
    if (dIter[v] < 6) {
      const w = p.slot[dCell[v] + DIR_OFF[dIter[v]++]];
      if (!(w >= 0 && w < n)) continue;
      if (dDisc[w] < 0) {
        dPar[w] = v;
        if (v === 0) rootKids++;
        dDisc[w] = timer;
        dLow[w] = timer++;
        seen++;
        dStack[sp++] = w;
      } else if (w !== dPar[v] && dDisc[w] < dLow[v]) {
        dLow[v] = dDisc[w];
      }
      continue;
    }
    sp--;
    const par = dPar[v];
    if (par >= 0) {
      if (dLow[v] < dLow[par]) dLow[par] = dLow[v];
      if (par !== 0 && dLow[v] >= dDisc[par]) p.cut[par] = 1;
    }
  }
  if (rootKids > 1) p.cut[0] = 1;

  // An unreachable cell would mean the cluster is already broken, which can only happen through a
  // bug. Silently returning a wrong cut set would hide it, so freeze everything instead: the game
  // stalls loudly rather than desyncing quietly.
  if (seen !== n) p.cut.fill(1, 0, n);
  return p.cut;
}

/** Can the top tile be lifted off `k` without splitting the cluster? */
export function liftable(p: Position, k: number): boolean {
  if (p.occ[k] > 1) return true; // something stays behind, so the cell-graph is unchanged
  return computeCuts(p)[p.slot[k]] === 0;
}

/** The cut vertices as cells. Tests and the audit only. */
export function cutCells(p: Position): Set<number> {
  const cuts = computeCuts(p);
  const out = new Set<number>();
  for (let i = 0; i < p.n; i++) if (cuts[i]) out.add(p.list[i]);
  return out;
}

/** Is the occupied set one connected component? The game never needs to ask — generation cannot
 *  produce a broken cluster — but the independent audit does. */
export function connected(p: Position): boolean {
  if (p.n <= 1) return true;
  const seen = new Set<number>([p.list[0]]);
  const stack = [p.list[0]];
  while (stack.length) {
    const cur = stack.pop()!;
    for (let d = 0; d < 6; d++) {
      const nb = cur + DIR_OFF[d];
      if (p.occ[nb] !== 0 && !seen.has(nb)) {
        seen.add(nb);
        stack.push(nb);
      }
    }
  }
  return seen.size === p.n;
}

/**
 * The slide test: can a ground tile move from `from` in direction `d`, with `ignore` (its own
 * origin) treated as already vacated? Both flanks occupied is too tight to squeeze through; both
 * flanks empty means letting go of the cluster mid-slide. Exactly one occupied is the legal case,
 * which is why this is a xor.
 */
export function canSlideDir(p: Position, from: number, d: number, ignore: number): boolean {
  const a = gateA(from, d);
  const b = gateB(from, d);
  const oa = p.occ[a] !== 0 && a !== ignore;
  const ob = p.occ[b] !== 0 && b !== ignore;
  return oa !== ob;
}

/** The same test named by destination, for the tests and the audit. */
export function canSlide(p: Position, from: number, to: number, ignore = -1): boolean {
  const d = dirBetween(from, to);
  return d >= 0 && canSlideDir(p, from, d, ignore);
}

/**
 * The climb test. A Climber goes over the top rather than between, so what stops it is a pair of
 * flanking stacks taller than both the height it is leaving and the height it lands on.
 * `fromHeight` is the height of the origin cell AFTER the climber has been lifted off it.
 */
export function canClimbDir(p: Position, from: number, d: number, fromHeight: number, ignore: number): boolean {
  const a = gateA(from, d);
  const b = gateB(from, d);
  const ha = a === ignore ? 0 : p.occ[a];
  const hb = b === ignore ? 0 : p.occ[b];
  const to = from + DIR_OFF[d];
  const hTo = to === ignore ? 0 : p.occ[to];
  return Math.min(ha, hb) <= Math.max(fromHeight, hTo);
}

// ---------------------------------------------------------------- placement

/** Every cell a new tile could legally be set down in, for the side to move. */
export function placementCells(p: Position): number[] {
  if (p.n === 0) return [key(0, 0)];
  if (p.n === 1) return neighbours(p.list[0]).sort((a, b) => a - b);

  const seat = p.turn;
  const out: number[] = [];
  const mark = ++stamp;
  for (let i = 0; i < p.n; i++) {
    const k = p.list[i];
    if ((p.topT[k] & 1) !== seat) continue;
    for (let d = 0; d < 6; d++) {
      const nb = k + DIR_OFF[d];
      if (p.occ[nb] !== 0 || seenAt[nb] === mark) continue;
      seenAt[nb] = mark;
      let clean = true;
      for (let e = 0; e < 6; e++) {
        const nn = nb + DIR_OFF[e];
        if (p.occ[nn] !== 0 && (p.topT[nn] & 1) !== seat) {
          clean = false;
          break;
        }
      }
      if (clean) out.push(nb);
    }
  }
  out.sort((a, b) => a - b);
  return out;
}

/** How many tiles this seat has already put down. */
export function placedCount(p: Position, seat: number): number {
  let total = 0;
  for (let k = 0; k < KIND_COUNT; k++) total += p.mode.supply[k] - p.hands[seat][k];
  return total;
}

/**
 * The Matriarch deadline. She must be down by a side's `matriarchBy`-th placement, so on exactly
 * that placement she is the only tile that may be played — and until she is down, nothing at all
 * may move.
 */
export function matriarchForced(p: Position, seat: number): boolean {
  return p.matriarch[seat] < 0 && placedCount(p, seat) >= p.mode.matriarchBy - 1;
}

// ---------------------------------------------------------------- movement laws

/** Where the top tile of `from` can go. */
export function destinations(p: Position, from: number): number[] {
  const out: number[] = [];
  pushDestinations(p, from, out);
  return out;
}

/** The allocation-free form: appends destination cells to `out`. */
export function pushDestinations(p: Position, from: number, out: number[]): void {
  const t = p.topT[from];
  if (t < 0) return;
  // A tile that has climbed is standing ON the cluster rather than slotted into it, so only the
  // Climber's law can describe how it gets down again.
  if (p.occ[from] > 1) return climbTo(p, from, out);
  switch (tileKind(t)) {
    case MATRIARCH:
    case TENDER:
      return stepTo(p, from, out);
    case STRIDER:
      return strideTo(p, from, out);
    case WEAVER:
      return weaveTo(p, from, out);
    case VAULTER:
      return vaultTo(p, from, out);
    case CLIMBER:
      return climbTo(p, from, out);
  }
}

function stepTo(p: Position, from: number, out: number[]): void {
  for (let d = 0; d < 6; d++) {
    const nb = from + DIR_OFF[d];
    if (p.occ[nb] !== 0) continue;
    if (!canSlideDir(p, from, d, from)) continue;
    out.push(nb);
  }
}

// A stamped visited-set: one write to mark a cell, and a bumped counter to forget the whole set.
// The alternative — a fresh Set per call — allocates twice per piece per node, which at a hundred
// thousand nodes a second is most of the garbage the search would ever make.
const seenAt = new Int32Array(CELLS);
let stamp = 0;
const walkQueue = new Int32Array(MAX_CELLS * 8);
const weavePath = new Int32Array(4);
const weaveEnds: number[] = [];

function strideTo(p: Position, from: number, out: number[]): void {
  const mark = ++stamp;
  seenAt[from] = mark;
  let tail = 0;
  let head = 0;
  walkQueue[tail++] = from;
  while (head < tail) {
    const cur = walkQueue[head++];
    for (let d = 0; d < 6; d++) {
      const nb = cur + DIR_OFF[d];
      if (p.occ[nb] !== 0 || seenAt[nb] === mark) continue;
      if (!canSlideDir(p, cur, d, from)) continue;
      seenAt[nb] = mark;
      out.push(nb);
      if (tail < walkQueue.length) walkQueue[tail++] = nb;
    }
  }
}

function weaveTo(p: Position, from: number, out: number[]): void {
  weavePath[0] = from;
  weaveEnds.length = 0;
  weaveWalk(p, from, from, 1);
  for (let i = 0; i < weaveEnds.length; i++) out.push(weaveEnds[i]);
}

function weaveWalk(p: Position, from: number, cur: number, depth: number): void {
  for (let d = 0; d < 6; d++) {
    const nb = cur + DIR_OFF[d];
    if (p.occ[nb] !== 0) continue;
    let repeat = false;
    for (let i = 0; i < depth; i++) if (weavePath[i] === nb) repeat = true;
    if (repeat) continue;
    if (!canSlideDir(p, cur, d, from)) continue;
    if (depth === 3) {
      if (weaveEnds.indexOf(nb) < 0) weaveEnds.push(nb);
      continue;
    }
    weavePath[depth] = nb;
    weaveWalk(p, from, nb, depth + 1);
  }
}

function vaultTo(p: Position, from: number, out: number[]): void {
  for (let d = 0; d < 6; d++) {
    const off = DIR_OFF[d];
    let cur = from + off;
    if (p.occ[cur] === 0) continue; // a leap must clear at least one tile
    while (p.occ[cur] !== 0) cur += off;
    out.push(cur);
  }
}

function climbTo(p: Position, from: number, out: number[]): void {
  const fromHeight = p.occ[from] - 1;
  // Only a climber standing on the ground vacates its cell; one that is on top of a stack leaves
  // the cell occupied, which is exactly the difference between stepping off the edge of the
  // cluster and stepping down off a stack.
  const ignore = fromHeight === 0 ? from : -1;
  for (let d = 0; d < 6; d++) {
    const nb = from + DIR_OFF[d];
    if (p.occ[nb] === 0) {
      let touches = false;
      for (let e = 0; e < 6; e++) {
        const nn = nb + DIR_OFF[e];
        if (nn !== ignore && p.occ[nn] !== 0) {
          touches = true;
          break;
        }
      }
      if (!touches) continue;
    }
    if (!canClimbDir(p, from, d, fromHeight, ignore)) continue;
    out.push(nb);
  }
}

// ---------------------------------------------------------------- the Tender's lift

/** The (tile, destination) pairs a Tender standing on `t` could lift and set down. */
export function shunts(p: Position, t: number): Array<{ from: number; to: number }> {
  const out: Array<{ from: number; to: number }> = [];
  if (p.occ[t] !== 1 || tileKind(p.topT[t]) !== TENDER || p.stunned(t)) return out;

  const empties: number[] = [];
  const lifts: number[] = [];
  for (let d = 0; d < 6; d++) {
    const nb = t + DIR_OFF[d];
    if (p.occ[nb] === 0) {
      empties.push(nb);
      continue;
    }
    if (p.occ[nb] !== 1) continue; // a stack is too heavy, and never a Climber sitting on one
    if (p.stunned(nb)) continue;
    if (!liftable(p, nb)) continue;
    lifts.push(nb);
  }
  // The lifted tile goes OVER the Tender, so it is in contact the whole way and lands touching
  // her — the slide rule never applies and connectivity after the landing is guaranteed.
  for (const from of lifts) for (const to of empties) out.push({ from, to });
  return out;
}

// ---------------------------------------------------------------- legal moves

/** Generate every legal move, appending to `out`. Order follows the dense occupied list, which is
 *  not canonical — `legalMoves` sorts, and the search does its own ordering. */
export function genMoves(p: Position, out: Move[]): Move[] {
  const seat = p.turn;

  const cells = placementCells(p);
  const forced = matriarchForced(p, seat);
  const hand = p.hands[seat];
  for (let kind = 0; kind < KIND_COUNT; kind++) {
    if (hand[kind] <= 0) continue;
    if (forced && kind !== MATRIARCH) continue;
    for (let i = 0; i < cells.length; i++) out.push({ t: 'place', kind, to: cells[i] });
  }

  if (p.matriarch[seat] >= 0) {
    computeCuts(p);
    const dests: number[] = [];
    for (let i = 0; i < p.n; i++) {
      const k = p.list[i];
      const t = p.topT[k];
      if ((t & 1) !== seat) continue;
      if (p.stunned(k)) continue;
      if (!liftable(p, k)) continue;
      dests.length = 0;
      pushDestinations(p, k, dests);
      for (let j = 0; j < dests.length; j++) out.push({ t: 'move', from: k, to: dests[j] });
      if (tileKind(t) === TENDER) {
        for (const s of shunts(p, k)) out.push({ t: 'shunt', tender: k, from: s.from, to: s.to });
      }
    }
  }

  if (out.length === 0) out.push({ t: 'pass' });
  return out;
}

/** Every legal move, in a canonical order. This is what the UI, the sim and the audit use. */
export function legalMoves(p: Position): Move[] {
  const out = genMoves(p, []);
  out.sort((a, b) => plyOf(a) - plyOf(b));
  return out;
}

export function isLegal(p: Position, m: Move): boolean {
  const target = plyOf(m);
  const moves = genMoves(p, []);
  for (let i = 0; i < moves.length; i++) if (plyOf(moves[i]) === target) return true;
  return false;
}

// ---------------------------------------------------------------- apply / undo

/** Apply a move that has already been checked legal. Returns the record undo() needs. */
export function apply(p: Position, m: Move): Undo {
  const rec: Undo = { m, stunA: p.stunA, stunB: p.stunB, stunUntil: p.stunUntil, passes: p.passes };
  switch (m.t) {
    case 'place':
      p.dropTile(m.to, tile(m.kind, p.turn));
      p.hands[p.turn][m.kind]--;
      if (m.kind === MATRIARCH) p.matriarch[p.turn] = m.to;
      p.passes = 0;
      break;
    case 'move': {
      const t = p.liftTile(m.from);
      p.dropTile(m.to, t);
      if (tileKind(t) === MATRIARCH) p.matriarch[tileSeat(t)] = m.to;
      p.passes = 0;
      break;
    }
    case 'shunt': {
      const t = p.liftTile(m.from);
      p.dropTile(m.to, t);
      if (tileKind(t) === MATRIARCH) p.matriarch[tileSeat(t)] = m.to;
      p.stunA = m.to;
      p.stunB = m.tender;
      p.stunUntil = p.ply + 3;
      p.passes = 0;
      break;
    }
    case 'pass':
      p.passes++;
      break;
  }
  p.turn = p.turn === 0 ? 1 : 0;
  p.ply++;
  p.invalidate();
  return rec;
}

export function undo(p: Position, rec: Undo): void {
  const m = rec.m;
  p.ply--;
  p.turn = p.turn === 0 ? 1 : 0;
  switch (m.t) {
    case 'place':
      p.liftTile(m.to);
      p.hands[p.turn][m.kind]++;
      if (m.kind === MATRIARCH) p.matriarch[p.turn] = -1;
      break;
    case 'move':
    case 'shunt': {
      const t = p.liftTile(m.to);
      p.dropTile(m.from, t);
      if (tileKind(t) === MATRIARCH) p.matriarch[tileSeat(t)] = m.from;
      break;
    }
    case 'pass':
      break;
  }
  p.stunA = rec.stunA;
  p.stunB = rec.stunB;
  p.stunUntil = rec.stunUntil;
  p.passes = rec.passes;
  p.invalidate();
}

// ---------------------------------------------------------------- resolution

/** How many of a Matriarch's six sides are occupied. */
export function closedSides(p: Position, seat: number): number {
  const at = p.matriarch[seat];
  if (at < 0) return 0;
  let n = 0;
  for (let d = 0; d < 6; d++) if (p.occ[at + DIR_OFF[d]] !== 0) n++;
  return n;
}

/** Is this seat's Matriarch surrounded, by this mode's definition of surrounded? */
export const surrounded = (p: Position, seat: number): boolean =>
  p.matriarch[seat] >= 0 && closedSides(p, seat) >= p.mode.surroundAt;

export type Reason = 'surround' | 'both' | 'stalemate' | 'repetition' | 'cap' | 'walkover';

export type Outcome =
  | { readonly over: false }
  | { readonly over: true; readonly winner: 0 | 1 | -1; readonly reason: Reason };

export const PLAYING: Outcome = { over: false };

export function outcome(p: Position): Outcome {
  const a = surrounded(p, 0);
  const b = surrounded(p, 1);
  if (a && b) return { over: true, winner: -1, reason: 'both' };
  if (a) return { over: true, winner: 1, reason: 'surround' };
  if (b) return { over: true, winner: 0, reason: 'surround' };
  if (p.passes >= 2) return { over: true, winner: -1, reason: 'stalemate' };
  if (p.ply >= p.mode.plyCap) return { over: true, winner: -1, reason: 'cap' };
  return PLAYING;
}

/**
 * A translation-invariant key for the position, used for the threefold-repetition draw and as the
 * search's transposition key. The cluster floats — a position shifted two hexes east IS the same
 * position — so the bounding box is normalised to the origin before hashing. The hold flags are in
 * the key because they change what is legal, and a draw that one peer sees and the other does not
 * is a desync at the worst possible moment.
 */
export function positionKey(p: Position): string {
  if (p.n === 0) return `e${p.turn}`;
  let minQ = Infinity;
  let minR = Infinity;
  for (let i = 0; i < p.n; i++) {
    const k = p.list[i];
    const q = keyQ(k);
    const r = keyR(k);
    if (q < minQ) minQ = q;
    if (r < minR) minR = r;
  }
  const parts: string[] = [];
  for (let i = 0; i < p.n; i++) {
    const k = p.list[i];
    parts.push(`${keyQ(k) - minQ},${keyR(k) - minR}:${p.stack(k).join('.')}`);
  }
  parts.sort();
  // The hold has to name WHICH cells are frozen, in the same normalised frame as the tiles, and how
  // much longer for. An earlier cut wrote `stunA - stunB`, which looks like it identifies the pair
  // and does not: the two are always neighbours, so that difference takes exactly six values — it
  // records the DIRECTION of the freeze and nothing else. Two positions with identical occupancy
  // and different frozen tiles then hashed equal, and the threefold rule fired on a position that
  // had never repeated. A false draw is deterministic on both peers, so it is not a desync; it is
  // just wrong, and it defeats the reason the key exists.
  const rel = (k: number): string => `${keyQ(k) - minQ},${keyR(k) - minR}`;
  const hold = p.ply < p.stunUntil ? `${rel(p.stunA)}/${rel(p.stunB)}/${p.stunUntil - p.ply}` : '';
  return `${p.turn}|${hold}|${parts.join(';')}`;
}

/** The empty cells touching the cluster — what the layout has to keep on screen alongside it. */
export function frontier(p: Position): number[] {
  if (p.n === 0) return [key(0, 0)];
  const out = new Set<number>();
  for (let i = 0; i < p.n; i++) for (let d = 0; d < 6; d++) {
    const nb = p.list[i] + DIR_OFF[d];
    if (p.occ[nb] === 0) out.add(nb);
  }
  return [...out];
}

/** The six cells around a Matriarch. Used by the search's forward prune and by the HUD meter. */
export function ring(p: Position, seat: number): number[] {
  const at = p.matriarch[seat];
  return at < 0 ? [] : neighbours(at);
}
